import { DateField } from "@equidefi/ui/forms";
import type { DateFieldProps } from "@equidefi/ui/forms/DateField";
import type { FieldPath, FieldValues } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";

export type SmartDatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<DateFieldProps, "name" | "value" | "onChange" | "onBlur" | "error" | "isInvalid"> & {
  name: TName;
  /**
   * How the picked date is stored in form state. Consumers that send the value
   * straight to the API as a filter want an ISO string; ones that compute with
   * it (min/max bounds, comparisons) want the Date itself.
   */
  valueAs?: "date" | "iso";
};

function SmartDatePicker<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, valueAs = "date", ...dateProps }: SmartDatePickerProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues, TName>({ name, control });

  return (
    <DateField
      name={field.name}
      ref={field.ref}
      value={field.value ?? null}
      onChange={(date?: Date) => field.onChange(valueAs === "iso" ? date?.toISOString() : (date ?? undefined))}
      onBlur={field.onBlur}
      isInvalid={!!error}
      error={error?.message}
      {...dateProps}
    />
  );
}

export default SmartDatePicker;
